import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { restaurantApi } from '../services/api';
import {
  X,
  ChevronRight,
  LayoutDashboard,
  DollarSign,
  UtensilsCrossed,
  Users,
  Calendar,
  Settings,
  Sparkles,
  Rocket,
  Building2,
  Package,
  Factory,
  Ship,
} from 'lucide-react';

interface TourStep {
  title: string;
  description: string;
  icon: typeof LayoutDashboard;
  color: string;
}

// The tour promises what the account actually has: a CPG brand hears about
// wholesale, imports and production — never reservations; a restaurant only
// hears about the modules that are on. Same component, data-driven steps.
function stepsFor(settings: any): TourStep[] {
  const s = settings || {};
  const modules = (s.modules || {}) as Record<string, boolean>;
  const on = (key: string, dflt = true) => (typeof modules[key] === 'boolean' ? modules[key] : dflt);
  const isCpg = s.businessType === 'cpg' || s.businessKind === 'cpg' || !!s.supplyRatios || !!s.channelForecast;

  const welcome: TourStep = {
    title: 'Dashboard Overview',
    description: 'Your command center. See the day\'s numbers and what needs attention at a glance.',
    icon: LayoutDashboard,
    color: 'bg-indigo-500',
  };
  const settingsStep: TourStep = {
    title: 'Settings',
    description: 'Configure your business, connect payments, and customize your setup.',
    icon: Settings,
    color: 'bg-slate-500',
  };

  if (isCpg) {
    return [
      welcome,
      {
        title: 'Wholesale & Accounts',
        description: 'POs, invoices and deductions live in Wholesale — Accounts lays the same customers out as a pipeline, lead to won.',
        icon: Building2,
        color: 'bg-emerald-500',
      },
      {
        title: 'Products & Inventory',
        description: 'Your catalog and stock: what you sell, what\'s on hand, and what each unit costs.',
        icon: Package,
        color: 'bg-amber-500',
      },
      ...(on('production', false) ? [{
        title: 'Production',
        description: 'Orders from every channel roll up into one make list with destinations and deadlines.',
        icon: Factory,
        color: 'bg-violet-500',
      }] : []),
      {
        title: 'Imports & Costs',
        description: 'Record inbound shipments and their lots — landed cost per unit and the organic balance build from them.',
        icon: Ship,
        color: 'bg-rose-500',
      },
      settingsStep,
    ];
  }

  return [
    welcome,
    ...(on('sales') ? [{
      title: 'Sales & Orders',
      description: 'Track sales performance, view order history, and analyze trends over time.',
      icon: DollarSign,
      color: 'bg-emerald-500',
    }] : []),
    ...(on('menu') ? [{
      title: 'Menu Management',
      description: 'Add items, set prices, manage modifiers, and mark items as 86\'d instantly.',
      icon: UtensilsCrossed,
      color: 'bg-amber-500',
    }] : []),
    ...(on('production', false) ? [{
      title: 'Production',
      description: 'Orders from every channel roll up into one make list with destinations and deadlines.',
      icon: Factory,
      color: 'bg-violet-500',
    }] : []),
    ...(on('team') ? [{
      title: 'Staff & Shifts',
      description: 'Manage your team, schedule shifts, track hours, and process payroll.',
      icon: Users,
      color: 'bg-violet-500',
    }] : []),
    ...(on('frontOfHouse') ? [{
      title: 'Reservations',
      description: 'Handle bookings, manage waitlists, and track guest preferences.',
      icon: Calendar,
      color: 'bg-rose-500',
    }] : []),
    settingsStep,
  ];
}

const STORAGE_KEY = 'mise_dashboard_tour_completed';

export default function WelcomeTour() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // "Seen it" lives on the ACCOUNT (settings.tourCompleted), not this browser —
  // localStorage alone made the tour reappear in every incognito window and on
  // every new device. The local flag stays as a fast path and for offline.
  const { data: restaurant } = useQuery({
    queryKey: ['restaurant-tour'],
    queryFn: async () => (await restaurantApi.getCurrent()).data?.data || null,
    staleTime: Infinity,
  });

  // Steps match the account: CPG accounts tour wholesale/imports/production,
  // everyone else sees only the modules that are actually on.
  const TOUR_STEPS = useMemo(() => stepsFor(restaurant?.settings), [restaurant]);

  useEffect(() => {
    if (!restaurant) return;
    if ((restaurant.settings as any)?.tourCompleted) return;
    if (localStorage.getItem(STORAGE_KEY)) {
      // Seen here before the server flag existed — sync it up so other
      // browsers/devices stop showing it too.
      restaurantApi.updateSettings({ tourCompleted: true }).catch(() => undefined);
      return;
    }
    // Delay showing the tour slightly for better UX
    const timer = setTimeout(() => setIsOpen(true), 500);
    return () => clearTimeout(timer);
  }, [restaurant]);

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    restaurantApi.updateSettings({ tourCompleted: true }).catch(() => undefined);
    setIsOpen(false);
  };

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handleSkip = () => {
    handleClose();
  };

  if (!isOpen) return null;

  const step = TOUR_STEPS[Math.min(currentStep, TOUR_STEPS.length - 1)];
  const isLastStep = currentStep >= TOUR_STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-8 text-white">
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {currentStep === 0 ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Welcome to Mise!</h2>
              <p className="text-white/80">Let's take a quick tour of your new dashboard</p>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 ${step.color} rounded-xl flex items-center justify-center`}>
                <step.icon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{step.title}</h2>
                <p className="text-white/80 text-sm">Feature {currentStep} of {TOUR_STEPS.length - 1}</p>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {currentStep === 0 ? (
            <div className="space-y-4">
              <p className="text-slate-600">
                We'll show you around the key features of your dashboard. This only takes about 30 seconds.
              </p>
              <div className="grid grid-cols-3 gap-3">
                {TOUR_STEPS.slice(1).map((s, idx) => (
                  <div key={idx} className="text-center p-3 bg-slate-50 rounded-xl">
                    <div className={`w-10 h-10 ${s.color} rounded-lg flex items-center justify-center mx-auto mb-2`}>
                      <s.icon className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-xs font-medium text-slate-700">{s.title}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-slate-600 text-lg">{step.description}</p>
          )}
        </div>

        {/* Progress & Actions */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
          <div className="flex items-center justify-between">
            {/* Progress dots */}
            <div className="flex gap-1.5">
              {TOUR_STEPS.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    idx === currentStep ? 'bg-indigo-600' : idx < currentStep ? 'bg-indigo-300' : 'bg-slate-300'
                  }`}
                />
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSkip}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium"
              >
                Skip Tour
              </button>
              <button
                onClick={handleNext}
                className="px-5 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
              >
                {isLastStep ? (
                  <>
                    <Rocket className="w-4 h-4" />
                    Get Started
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
